import { Component, EventEmitter, Inject, Input, Output, ViewChild, AfterViewInit, OnInit } from '@angular/core';
import { BASE_INDI_URL } from 'src/app/injection-tokens';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { OrderDetails } from '../order-details';
import {
  FollowUpNeededOptions,
  MancoOrderRequest,
  MancoType,
  MancoTypeOptions,
  parseFollowUpNeededOption,
  parseMancoType,
} from '../manco-order-request';
import { IndiCustomer, Address as SavedAddress } from '../../../salesorders/indi-customer';
import { User } from '../../../salesorders/user';
import { deliveryDateValidator, oneYearAheadISO, todayISO } from '../../../../utils/delivery-date-validator';
import { followUpNeededValidator, mancoTypeValidator } from './manco-request-validators';
import { NewMancoFormOrderlinesComponent } from './new-manco-form-orderlines/new-manco-form-orderlines.component';
import { NewMancoFormDeliveryaddressComponent } from './new-manco-form-deliveryaddress/new-manco-form-deliveryaddress.component';
import { MancoDeliveryAddressForm, MancoOrderDetailsForm, MancoOrderlineForm } from './manco-form-types';

export interface MancoAddressOption {
  label: string;
  value: SavedAddress;
}

@Component({
  selector: 'app-new-manco-form',
  templateUrl: './new-manco-form.component.html',
  styleUrls: ['./new-manco-form.component.scss'],
})
export class NewMancoFormComponent implements OnInit, AfterViewInit {
  @Input() orderDetails!: OrderDetails;
  @Input() indiCustomer?: IndiCustomer;
  @Input() users: User[] = [];
  @Output() submitMancoRequest = new EventEmitter<MancoOrderRequest>();

  @ViewChild(NewMancoFormOrderlinesComponent) orderlinesComponent!: NewMancoFormOrderlinesComponent;
  @ViewChild(NewMancoFormDeliveryaddressComponent) deliveryAddressComponent!: NewMancoFormDeliveryaddressComponent;

  requiredValidator = Validators.required.bind(null);

  mancoTypeOptions = MancoTypeOptions;
  followUpNeededOptions = FollowUpNeededOptions;

  form!: FormGroup<MancoOrderDetailsForm>;

  addressOptions: MancoAddressOption[] = [];

  isUserPreselected: boolean = false;
  isUserListSynthesized: boolean = false;

  private viewInitialized = false;

  constructor(
    @Inject(BASE_INDI_URL) readonly baseIndiUrl: string,
    private fb: FormBuilder
  ) {}

  get orderlines(): FormArray<FormGroup<MancoOrderlineForm>> {
    return this.form.get('orderlines') as FormArray<FormGroup<MancoOrderlineForm>>;
  }

  get isPrivateCustomer(): boolean {
    return this.indiCustomer?.type === 'CT_PRIVATE';
  }

  // Business unless explicitly a private (B2C) customer; false while the
  // customer is not loaded yet (the safer, consumer ATP routing). Note this is
  // NOT simply !isPrivateCustomer: an absent customer must not read as business.
  get isBusinessCustomer(): boolean {
    return !!this.indiCustomer && this.indiCustomer.type !== 'CT_PRIVATE';
  }

  /**
   * Recomputed on every read rather than cached in ngOnInit: a service-desk tab can stay open past midnight, and a
   * frozen lower bound would accept the now-stale prefill and let orders-service reject it with a 400 instead.
   * Today stays allowed: it is what a manco has always been, INDI erred so the replacement ships as soon as possible.
   */
  get datePickerMin(): string {
    return todayISO();
  }

  get datePickerMax(): string {
    return oneYearAheadISO();
  }

  isFormValid(): boolean {
    if (!this.viewInitialized) {
      return false;
    }
    return (
      !this.orderlinesComponent.valid ||
      !this.deliveryAddressComponent.valid ||
      !this.form.get('mancoType')?.valid ||
      !this.form.get('userUUID')?.valid ||
      !this.form.get('fixedDeliveryDate')?.valid ||
      !this.form.get('followUpNeeded')?.valid
    );
  }

  ngOnInit(): void {
    this.form = this.createFormGroup();
  }

  ngAfterViewInit(): void {
    this.populateFormWithOrderDetails();
    this.setOrderlines();
    this.viewInitialized = true;
  }

  createFormGroup(): FormGroup<MancoOrderDetailsForm> {
    return this.fb.group<MancoOrderDetailsForm>({
      orderlines: this.fb.array<FormGroup<MancoOrderlineForm>>([]),
      mancoType: new FormControl<string>('', {
        nonNullable: true,
        validators: [this.requiredValidator, mancoTypeValidator()],
      }),
      customerRef: new FormControl<string>('', { nonNullable: true, validators: [Validators.maxLength(36)] }),
      note: new FormControl<string>('', { nonNullable: true }),
      deliveryAddress: this.fb.group<MancoDeliveryAddressForm>({
        company: new FormControl<string>('', { nonNullable: true }),
        firstName: new FormControl<string>('', { nonNullable: true }),
        lastName: new FormControl<string>('', { nonNullable: true, validators: [this.requiredValidator] }),
        address: new FormControl<string>('', { nonNullable: true, validators: [this.requiredValidator] }),
        postalCode: new FormControl<string>('', { nonNullable: true, validators: [this.requiredValidator] }),
        city: new FormControl<string>('', { nonNullable: true, validators: [this.requiredValidator] }),
        country: new FormControl<string>('', {
          nonNullable: true,
          validators: [this.requiredValidator, Validators.pattern(/^(NL|DE)$/)],
        }),
        email: new FormControl<string>('', { nonNullable: true }),
        telephoneNumber: new FormControl<string>('', { nonNullable: true }),
        switchToDayShipping: new FormControl<boolean>(false, { nonNullable: true }),
      }),
      ishShippingMethodID: new FormControl<string>('', { nonNullable: true }),
      fixedDeliveryDate: new FormControl<string>(this.datePickerMin, {
        nonNullable: true,
        validators: [
          this.requiredValidator,
          deliveryDateValidator(
            () => this.datePickerMin,
            () => this.datePickerMax
          ),
        ],
      }),
      followUpNeeded: new FormControl<boolean | string>('', {
        nonNullable: true,
        validators: [this.requiredValidator, followUpNeededValidator()],
      }),
      userUUID: new FormControl<string>('', { nonNullable: true, validators: [this.requiredValidator] }),
    });
  }

  populateFormWithOrderDetails(): void {
    const deliveryAddress = this.orderDetails.deliveryAddress;
    this.addressOptions = this.buildAddressOptions();

    this.ensurePreselectableUserExists();
    this.applyPrivateCustomerUserRule();

    const preselectedUserUUID = this.findPreselectableUserUUID();
    this.isUserPreselected = preselectedUserUUID !== '';

    this.form.patchValue({
      ishShippingMethodID: this.orderDetails.shippingMethod,
      mancoType: '',
      customerRef: this.orderDetails.orderRef,
      note: '',
      deliveryAddress: {
        company: this.orderDetails.customerDetails.companyName,
        firstName: deliveryAddress.firstName ?? '',
        lastName: deliveryAddress.lastName ?? '',
        address: deliveryAddress.address,
        postalCode: deliveryAddress.postalCode,
        city: deliveryAddress.city,
        country: deliveryAddress.country,
        telephoneNumber: deliveryAddress.telephoneNumber ?? '',
        email: deliveryAddress.emailAddress ?? '',
      },
      userUUID: preselectedUserUUID,
    });

    if (preselectedUserUUID) {
      this.applyUserToFormEmail(preselectedUserUUID);
    }
  }

  setOrderlines(): void {
    const orderlinesArray = this.form.get('orderlines') as FormArray;
    this.orderDetails.orderlines.forEach((ol, index) => {
      orderlinesArray.push(this.orderlinesComponent.createOriginalOrderlineGroup(ol, index));
    });
  }

  onUserChange(): void {
    const userUUID = this.form.get('userUUID')?.value ?? '';
    this.applyUserToFormEmail(userUUID);
  }

  onAddressSelectionChange(index: number): void {
    if (index < 0 || index >= this.addressOptions.length) {
      return;
    }
    this.applyAddressToForm(this.addressOptions[index].value);
  }

  onSubmit(): void {
    const orderlines = this.orderlinesComponent.getOrderlines();
    const deliveryAddress = this.deliveryAddressComponent.address;
    const selectedUser = this.findSelectedUser();

    const newMancoRequest: MancoOrderRequest = {
      forOrderNumber: this.orderDetails.orderNumber,
      forCustomerNumber: this.orderDetails.customerDetails.customerNo,
      mancoType: parseMancoType(this.form.value.mancoType) ?? MancoType.MancoSupplier,
      freshdeskTicketNumber: 'Will be written by owner of this component',
      ishShippingMethodID: this.form.value.ishShippingMethodID ?? 'shipping-standard-250',
      deliveryAddress: {
        firstName: deliveryAddress.firstName,
        lastName: deliveryAddress.lastName,
        telephoneNumber: deliveryAddress.telephoneNumber,
        emailAddress: deliveryAddress.emailAddress,
        address: deliveryAddress.address,
        postalCode: deliveryAddress.postalCode,
        city: deliveryAddress.city,
        country: deliveryAddress.country,
        company: deliveryAddress.company,
      },
      orderlines,
      note: this.form.get('note')?.value ?? '',
      fixedDeliveryDate: this.form.get('fixedDeliveryDate')?.value,
      followUpNeeded: parseFollowUpNeededOption(this.form.value.followUpNeeded) ?? true,
      customerRef: this.form.get('customerRef')?.value ?? '',
      userUUID: this.isPrivateCustomer ? undefined : this.form.get('userUUID')?.value,
      invoiceEmailAddress: this.isPrivateCustomer
        ? deliveryAddress.emailAddress
        : (selectedUser?.invoicesEmailAddress ?? selectedUser?.loginEmailAddress),
      userDisplayName: this.isPrivateCustomer ? undefined : selectedUser?.fullName,
    };

    this.submitMancoRequest.emit(newMancoRequest);
  }

  invalidOrderlines(): boolean {
    return !this.orderlinesComponent.valid;
  }

  resetForm(): void {
    this.orderlinesComponent.reset();
    this.form.reset();
    // reset() restores this nonNullable control's construction-time value, which may be a stale earlier day;
    // re-stamp it with the current today so a long-lived tab does not reset to a date the validator rejects.
    this.form.get('fixedDeliveryDate')?.setValue(this.datePickerMin);
  }

  private buildAddressOptions(): MancoAddressOption[] {
    const options: MancoAddressOption[] = [];

    options.push({
      label: 'Origineel besteladres',
      value: {
        type: 'OriginalOrder',
        companyName: this.orderDetails.customerDetails.companyName,
        firstName: this.orderDetails.deliveryAddress.firstName ?? '',
        lastName: this.orderDetails.deliveryAddress.lastName ?? '',
        address: this.orderDetails.deliveryAddress.address,
        postalCode: this.orderDetails.deliveryAddress.postalCode,
        city: this.orderDetails.deliveryAddress.city,
        countryCode: this.orderDetails.deliveryAddress.country,
        nightShipping: false,
      },
    });

    (this.indiCustomer?.addresses ?? []).forEach(addr => {
      options.push({
        label: this.formatAddressLabel(addr),
        value: addr,
      });
    });

    return options;
  }

  private findPreselectableUserUUID(): string {
    const original = this.orderDetails.originalOrderUserUUID;
    if (original && this.users.some(u => u.uuid === original)) {
      return original;
    }
    return '';
  }

  /**
   * Row 3 of the no-uuids matrix: the order itself carries the original buyer UUID,
   * but indi-customers returns no users for this customer. Surface that UUID as a
   * single locked dropdown entry so the manco can still be attributed to its real buyer.
   */
  private ensurePreselectableUserExists(): void {
    this.isUserListSynthesized = false;
    const original = this.orderDetails.originalOrderUserUUID;
    if (!original || this.users.length > 0) {
      return;
    }
    this.users = [this.synthesizePlaceholderUser(original)];
    this.isUserListSynthesized = true;
  }

  private synthesizePlaceholderUser(uuid: string): User {
    const firstName = this.orEmpty(this.orderDetails.customerDetails.firstName);
    const lastName = this.orEmpty(this.orderDetails.customerDetails.lastName);
    const composedName = `${firstName} ${lastName}`.trim();
    return {
      uuid,
      fullName: composedName !== '' ? composedName : 'Originele besteller',
      loginEmailAddress: this.orderDetails.deliveryAddress.emailAddress ?? '',
    };
  }

  /**
   * Private (B2C) customers have no registered user; the manco is booked on the customer number, so the
   * "Manco op naam van" selection does not apply and must not block submission.
   */
  private applyPrivateCustomerUserRule(): void {
    const userControl = this.form.get('userUUID');
    if (!userControl) {
      return;
    }
    if (this.isPrivateCustomer) {
      userControl.clearValidators();
    } else {
      userControl.setValidators([this.requiredValidator]);
    }
    userControl.updateValueAndValidity();
  }

  private findSelectedUser(): User | undefined {
    const uuid = this.form.get('userUUID')?.value;
    return uuid ? this.users.find(u => u.uuid === uuid) : undefined;
  }

  private applyUserToFormEmail(userUUID: string): void {
    const user = this.users.find(u => u.uuid === userUUID);
    if (!user) {
      return;
    }
    this.form.get('deliveryAddress.email')?.setValue(user.loginEmailAddress);
  }

  private applyAddressToForm(address: SavedAddress): void {
    const addressGroup = this.form.get('deliveryAddress') as FormGroup<MancoDeliveryAddressForm>;
    addressGroup.patchValue({
      company: address.companyName,
      firstName: address.firstName,
      lastName: address.lastName,
      address: address.address,
      postalCode: address.postalCode,
      city: address.city,
      country: this.normalizeCountry(address.countryCode),
    });
  }

  private normalizeCountry(countryCode: string): string {
    if (countryCode === 'NLD') {
      return 'NL';
    }
    if (countryCode === 'DEU') {
      return 'DE';
    }
    return countryCode;
  }

  private formatAddressLabel(addr: SavedAddress): string {
    // Private (B2C) customers have addresses without a company name (companyName comes back null).
    const company = this.orEmpty(addr.companyName).trim();
    const street = addr.address.trim();
    const city = addr.city.trim();
    const segments = [company, street, city].filter(s => s.length > 0);
    return segments.length > 0 ? segments.join(', ') : 'Onbekend adres';
  }

  // SavedAddress types companyName as string, but indi-customers returns null for private customers.
  private orEmpty(value: string | null | undefined): string {
    return value ?? '';
  }
}
